import { Box, Card, CardContent, CardHeader, Link, Skeleton, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { QueueRow } from "@xop/dashboard-shared";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

/**
 * The ONE card shell of the Company pulse. Every card on the landing page —
 * the pulse queues (SLA breaches, Invoices due, …) and the Products & partners
 * watch cards (Licences, Reseller applications, Releases) — renders through
 * this, so they share one identity: a pastel system accent bar + header wash,
 * an uppercase system eyebrow, an optional count pill, an "Open <System> →"
 * link, and a "View all →" footer.
 *
 * Extracted from `NeedsAttention`'s old `QueueSection` when the page went to
 * three columns (2026-09-15); the look is unchanged.
 *
 * The card FILLS its grid cell (`height: 100%`) and pins its footer to the
 * bottom, because the dashboard is a strict grid in which every card is as
 * tall as the tallest one — a short card gets space above its footer, never
 * a ragged bottom edge.
 */

export type SystemKey = "support" | "crm" | "sales" | "docs" | "marketing";

export interface SystemRef {
  /** Short, capitalised name shown in the eyebrow + header link. */
  label: string;
  /** Where "Open <System> →" sends the user. */
  href: string;
  /** Key into `theme.palette.system` — the pastel accent. */
  systemKey: SystemKey;
}

export function PulseCard({
  system,
  title,
  count,
  testId,
  footerHref,
  footerLabel = "View all →",
  children,
}: {
  system: SystemRef;
  title: string;
  /** Shown as a pill beside the header link when above zero. */
  count?: number | undefined;
  testId?: string | undefined;
  footerHref?: string | undefined;
  footerLabel?: string | undefined;
  children: ReactNode;
}): JSX.Element {
  const theme = useTheme();
  const accent = theme.palette.system[system.systemKey];
  return (
    <Card
      data-testid={testId ?? `queue-section-${slug(system.label)}-${slug(title)}`}
      data-pulse-card=""
      sx={{
        position: "relative",
        overflow: "hidden",
        height: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        boxShadow: theme.shadows[1],
        transition: "box-shadow 200ms ease-out, transform 200ms ease-out",
        "&:hover": { boxShadow: theme.shadows[3], transform: "translateY(-1px)" },
      }}
    >
      {/* Pastel system identity — a crisp accent bar at the very top plus
          a soft wash fading down behind the header. */}
      <Box aria-hidden sx={{ pointerEvents: "none", position: "absolute", insetInline: 0, top: 0, height: 6, bgcolor: accent }} />
      <Box
        aria-hidden
        sx={{
          pointerEvents: "none",
          position: "absolute",
          insetInline: 0,
          top: 0,
          height: 96,
          background: `linear-gradient(to bottom, ${alpha(accent, 0.3)}, transparent)`,
        }}
      />
      <CardHeader
        disableTypography
        // The horizontal padding tightens with the viewport: on the three-column
        // board below ~1000px a card is ~250-300px wide, and 20px of padding
        // each side is width the invoice number needs more (2026-09-16).
        sx={{ position: "relative", px: CARD_PX, py: 1.5, alignItems: "flex-start" }}
        title={
          <Box sx={{ display: "grid", gap: 0.25 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em", color: "text.disabled" }}>
              <Box aria-hidden sx={{ display: "inline-block", height: 6, width: 6, borderRadius: "50%", bgcolor: accent }} />
              {system.label}
            </Box>
            <Box sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.25, letterSpacing: "-0.01em", color: "text.primary" }}>
              {title}
            </Box>
          </Box>
        }
        action={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {count !== undefined && count > 0 && (
              <Box
                component="span"
                data-testid="pulse-card-count"
                sx={{
                  borderRadius: 999,
                  border: 1,
                  px: 0.75,
                  py: 0.25,
                  fontSize: "10.5px",
                  fontWeight: 500,
                  fontVariantNumeric: "tabular-nums",
                  color: "text.primary",
                  bgcolor: alpha(accent, 0.22),
                  borderColor: alpha(accent, 0.4),
                }}
              >
                {count}
              </Box>
            )}
            <Link
              component={RouterLink}
              to={system.href}
              underline="hover"
              data-testid={`queue-section-open-${slug(system.label)}`}
              sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary", whiteSpace: "nowrap", "&:hover": { color: "text.primary" } }}
            >
              Open {system.label} →
            </Link>
          </Box>
        }
      />
      <CardContent sx={{ position: "relative", p: 0, flex: 1, display: "flex", flexDirection: "column", "&:last-child": { pb: 0 } }}>
        {children}
        {footerHref && (
          <Box sx={{ mt: "auto", borderTop: 1, borderColor: "divider", px: 2.5, py: 1, textAlign: "right" }}>
            <Link
              component={RouterLink}
              to={footerHref}
              underline="hover"
              sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary", "&:hover": { color: "text.primary" } }}
            >
              {footerLabel}
            </Link>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/** A one-line state inside a card: the green all-clear, a quiet neutral, or
 * the amber "couldn't load". */
export function PulseCardMessage({
  tone = "neutral",
  testId,
  children,
}: {
  tone?: "good" | "neutral" | "warning";
  testId?: string | undefined;
  children: ReactNode;
}): JSX.Element {
  const color = tone === "good" ? "success.main" : tone === "warning" ? "warning.main" : "text.disabled";
  return (
    <Typography data-testid={testId} sx={{ px: CARD_PX, py: 2, fontSize: 12, color }}>
      {children}
    </Typography>
  );
}

export function PulseCardSkeleton({ rows = 3 }: { rows?: number }): JSX.Element {
  return (
    <Box data-testid="pulse-card-loading" sx={{ px: CARD_PX, py: 1.5, display: "grid", gap: 1.25 }}>
      {Array.from({ length: rows }, (_, i) => (
        <Box key={i} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
          <Skeleton variant="text" width={`${60 - i * 10}%`} />
          <Skeleton variant="text" width="18%" />
        </Box>
      ))}
    </Box>
  );
}


/** One horizontal padding scale for every part of a pulse card, so the header,
 *  the rows, the stat row and the empty states stay flush with each other as
 *  the board narrows. 20px is right on a wide card; on the three-column board
 *  under ~1000px it is width the content needs (2026-09-16). */
export const CARD_PX = { xs: 1.75, lg: 2.5 } as const;

/* ---------- stats strip ----------------------------------------------- */

export interface PulseStat {
  label: string;
  value: number;
  /** Colours the number when it is above zero. A zero is always quiet. */
  tone: "error" | "warning" | "neutral";
}

/** A row of big numbers under the header — the card's answer at a glance. */
export function PulseStats({ stats, testId }: { stats: readonly PulseStat[]; testId?: string }): JSX.Element {
  const theme = useTheme();
  return (
    <Box
      data-testid={testId}
      sx={{
        mx: CARD_PX,
        mb: 1.5,
        display: "grid",
        // WRAPS rather than shrinks. Three stats in a 250px card would be three
        // 70px slivers with every label clipped to "Wit…"; `auto-fit` drops them
        // to two rows instead, and the 1px gaps over a divider-coloured ground
        // draw the separators in BOTH directions (a `borderLeft` on every tile
        // but the first paints a stray line at the start of a wrapped row).
        gridTemplateColumns: `repeat(auto-fit, minmax(92px, 1fr))`,
        gap: "1px",
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "divider",
        overflow: "hidden",
      }}
    >
      {stats.map((stat) => {
        const hot = stat.value > 0 && stat.tone !== "neutral";
        const color = hot ? theme.palette[stat.tone === "error" ? "error" : "warning"].main : undefined;
        return (
          <Box
            key={stat.label}
            data-testid={`pulse-stat-${slug(stat.label)}`}
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: hot && color ? alpha(color, 0.08) : "surfaceSoft",
            }}
          >
            <Box
              sx={{
                fontFamily: theme.typography.h1.fontFamily,
                fontSize: 20,
                fontWeight: 600,
                lineHeight: 1.2,
                fontVariantNumeric: "tabular-nums",
                color: color ?? (stat.value > 0 ? "text.primary" : "text.disabled"),
              }}
            >
              {stat.value}
            </Box>
            <Box sx={{ fontSize: 11, color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {stat.label}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

/* ---------- rows ------------------------------------------------------- */

/** A queue row, plus the two things a watch card adds: an amber `warn` tone
 * and a small badge after the label ("Internal"). */
export interface PulseRow extends Omit<QueueRow, "tone"> {
  tone?: QueueRow["tone"] | "warn";
  badge?: string | undefined;
}

export function PulseRows({ rows }: { rows: readonly PulseRow[] }): JSX.Element {
  return (
    <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
      {rows.map((row) => (
        <PulseRowItem key={row.id} row={row} />
      ))}
    </Box>
  );
}

function PulseRowItem({ row }: { row: PulseRow }): JSX.Element {
  const metaColor = row.tone === "bad" ? "error.main" : row.tone === "warn" ? "warning.main" : "text.disabled";
  const inner = (
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, px: CARD_PX, py: 1.25 }}>
      <Box sx={{ minWidth: 0, flex: 1, display: "flex", alignItems: "center", gap: 0.75 }}>
        <Typography sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 14, color: "text.primary" }}>
          {row.label}
        </Typography>
        {row.badge && (
          <Box
            component="span"
            sx={{ flexShrink: 0, borderRadius: 999, border: 1, borderColor: "divider", px: 0.75, fontSize: 10, fontWeight: 500, color: "text.secondary" }}
          >
            {row.badge}
          </Box>
        )}
      </Box>
      {row.meta && (
        <Box component="span" sx={{ flexShrink: 0, fontSize: 11, color: metaColor, fontWeight: row.tone === "bad" || row.tone === "warn" ? 500 : 400 }}>
          {row.meta}
        </Box>
      )}
    </Box>
  );
  return (
    <Box component="li" sx={{ borderBottom: 1, borderColor: "divider", "&:last-of-type": { borderBottom: 0 } }}>
      {row.link ? (
        <Link
          component={RouterLink}
          to={row.link}
          underline="none"
          sx={{ display: "block", color: "inherit", transition: "background-color 150ms", "&:hover": { bgcolor: "surfaceSoft" } }}
        >
          {inner}
        </Link>
      ) : (
        inner
      )}
    </Box>
  );
}

export function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
